import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { CancelIcon } from '@chegg-ui/theme'
import { NOOP } from '@chegg-wt/core/constants/global'
import { closeModal } from '@chegg-wt/core/state/modals/actions'
import { modalTypeSelector } from '@chegg-wt/core/state/modals/selectors'
import * as Styled from './styled'
import { MODAL_CLOSE_BUTTON, MODAL_CONTENT, MODAL_WRAPPER } from './testIds'

interface Props {
  /** open/close state of modal */
  showModal: boolean
  /** close on click outside of modal */
  outsideClick?: boolean
  /** remmove padding from modal content */
  noPadding?: boolean
  /** close button UI */
  closeButton?: boolean
  /** close button color */
  closeButtonColor?: string
  /** top space */
  topSpace?: string
  /** enable scrolling behavior */
  enableScroll?: boolean
  /** modal that takes full mobile screen */
  fullMobileModal?: boolean
  fullWidth?: boolean
  maxWidth?: string
  Backdrop?: React.ReactElement<any> // TODO: we probably don't need this prop. need more investigation
  showBackdrop?: boolean
  onClose?: () => void
  ref?: React.Ref<HTMLDivElement>
  onKeyDown?: (e: React.KeyboardEvent) => void
  id?: string
  transparentBackground?: boolean
  isLoading?: boolean
  zIndex?: number
  maxHeight?: boolean
}

export const Modal: React.FC<Props> = React.forwardRef(
  (
    {
      children,
      showModal,
      noPadding = false,
      outsideClick,
      closeButton,
      topSpace,
      closeButtonColor,
      fullMobileModal,
      fullWidth = true,
      maxWidth,
      onClose,
      Backdrop = (props) => <Styled.DefaultBackdrop {...props} />,
      enableScroll = false,
      showBackdrop = true,
      onKeyDown = NOOP,
      id,
      transparentBackground,
      isLoading,
      zIndex,
      maxHeight,
    },
    ref
  ) => {
    const dispatch = useDispatch()
    const name = useSelector(modalTypeSelector)

    const handleCloseModal = () => {
      if (onClose) onClose()
      dispatch(closeModal(name))
    }

    return (
      <Styled.ModalWrapper
        backdrop={showBackdrop}
        show={showModal}
        enableScroll={enableScroll}
        onBackdropClick={outsideClick ? handleCloseModal : undefined}
        renderBackdrop={Backdrop}
        data-test={MODAL_WRAPPER}
      >
        <Styled.PositioningWrapper>
          <Styled.ModalContent
            fullMobileModal={fullMobileModal}
            maxWidth={maxWidth}
            noPadding={noPadding}
            fullWidth={fullWidth}
            data-test={MODAL_CONTENT}
            tabIndex={-1}
            ref={ref}
            onKeyDown={onKeyDown}
            id={id}
            transparentBackground={transparentBackground}
            maxHeight={maxHeight}
          >
            {closeButton && (
              <Styled.CloseButton
                screenReaderOnlyText="close modal"
                onClick={handleCloseModal}
                data-test={MODAL_CLOSE_BUTTON}
                closeButtonColor={closeButtonColor}
                topSpace={topSpace}
                isLoading={isLoading}
                zIndex={zIndex}
              >
                <CancelIcon />
              </Styled.CloseButton>
            )}
            {children}
          </Styled.ModalContent>
        </Styled.PositioningWrapper>
      </Styled.ModalWrapper>
    )
  }
)

export default Modal
